import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  SearchBar,
  Typography,
} from 'web-library';


import { makeStyles } from 'tss-react/mui';
import {
  TableActionTypes,
  TableState,
  useDataTable,
} from 'web-library/stories/organism/DataTable/DataTable';
import { FUNCTION_NAMES } from 'web-library/models/AuthorityFunctions';
import DownloadFromS3Action from '../../../components/buttons/downloadFromS3Action';
import DownloadAuditProgressModal from '../../../components/modals/DownloadAuditProgressModal';
import { dropDownMappingToSelect, useAPIDropdown } from '../../../hooks/dropdown';
import { candidateService } from '../../../api';
import { getIndexValue } from '../../../utils/ArrayUtils';
import { FileUploadState } from '../../../models/File';
import { RetrieveS3URLResponse } from '../../../api/document/files/api-services/model';
import { PaymentReportSearchRequest } from '../../../api/candidate/payment-report/model';
import { SearchDate } from '../../../models';
import { isDateValid, stringToSemanticDisplay, stringToShortTime } from '../../../utils/DateUtils';
import {
  examLevelColumn,
  paymentMethodColumn,
  paymentStatusColumn,
  privateCenterColumn,
  transactionTypeColumn,
} from './PaymentReportTableColumn';

const useStyles = makeStyles()((theme) =>
({
  select: {
    marginBottom: theme.spacing(3),
  },
  searchContainer: {
    paddingBottom: theme.spacing(2),
  },
  searchInput: {
    maxWidth: '100%',
  },
}));

interface TableColumnProps {
  data: {
    examLevel: { functionCode: []; typeMapping: { [key: string]: any } };
    paymentMethod: { functionCode: []; typeMapping: { [key: string]: any } };
    transactionType: { functionCode: []; typeMapping: { [key: string]: any } };
    privateCentre: { functionCode: []; typeMapping: { [key: string]: any } };
    paymentStatus: { functionCode: []; typeMapping: { [key: string]: any } };
  };
  classes: Record<string,string>;
}

const useDataTableColumns = ({ data, classes }: TableColumnProps) => {
  const { t } = useTranslation();
  const { state, dispatch: tableDispatch, tOptions } = useDataTable();

  const {
    examLevel: { functionCode: filterCodeExamLevel, typeMapping: filterMappingExamLevel },
    paymentMethod: {
      functionCode: filterCodePaymentMethod,
      typeMapping: filterMappingPaymentMethod,
    },
    transactionType: {
      functionCode: filterCodeTransactionType,
      typeMapping: filterMappingTransactionType,
    },
    privateCentre: {
      functionCode: filterCodePrivateCenter,
      typeMapping: filterMappingPrivateCenter,
    },
    paymentStatus: {
      functionCode: filterCodePaymentStatus,
      typeMapping: filterMappingPaymentStatus,
    },
  } = data;

  const tableColumns: MUIDataTableColumnDef[] = [
    transactionTypeColumn(
      classes,
      state,
      tableDispatch,
      t,
      filterCodeTransactionType,
      filterMappingTransactionType
    ),
    examLevelColumn(classes, state, tableDispatch, t, filterCodeExamLevel, filterMappingExamLevel),
    {
      name: 'candidateName',
      label: t('admin->>PaymentReport.CandidateName'),
      options: {
        sort: true,
        filter: false,
        customBodyRenderLite: (dataIndex: number) => (
          <Box>
            <Typography variant="body2">{state.data[dataIndex].candidateName}</Typography>
            <Typography variant="body2">{state.data[dataIndex].candidateId}</Typography>
          </Box>
        ),
      },
    },
    privateCenterColumn(
      classes,
      state,
      tableDispatch,
      t,
      filterCodePrivateCenter,
      filterMappingPrivateCenter
    ),
    {
      name: 'coordinatorName',
      label: t('admin->>PaymentReport.CoordinatorName'),
      options: {
        sort: true,
        filter: false,
        customBodyRender: (value: any) => (
          <Typography variant="body2">{value || 'N/A'}</Typography>
        ),
      },
    },
    {
      name: 'paymentDateTime',
      label: t('admin->>PaymentReport.PaymentDateTime'),
      options: {
        sort: true,
        filter: false,
        customBodyRender: (value: any) =>
          isDateValid(value) ? (
            <>
              <Typography variant="body2">{stringToSemanticDisplay(value)}</Typography>
              <Typography variant="body2">{stringToShortTime(value)}</Typography>
            </>
          ) : (
            <Typography variant="body2">N/A</Typography>
          ),
      },
    },
    paymentMethodColumn(
      classes,
      state,
      tableDispatch,
      t,
      filterCodePaymentMethod,
      filterMappingPaymentMethod
    ),
    paymentStatusColumn(
      classes,
      state,
      tableDispatch,
      t,
      filterCodePaymentStatus,
      filterMappingPaymentStatus
    ),
  ];

  return {
    tableColumns,
    state,
    tableDispatch,
    tOptions,
  };
};

const useRawFilters = () => {
  const transactionType = useAPIDropdown(
    () => candidateService.paymentReport.opadmin.useGetTransactionTypes(),
    true,
    dropDownMappingToSelect
  );

  const privateCentre = useAPIDropdown(
    () => candidateService.paymentReport.opadmin.useGetOrganisations(),
    true,
    dropDownMappingToSelect
  );

  const examLevel = useAPIDropdown(
    () => candidateService.paymentReport.opadmin.useGetExamLevels(FUNCTION_NAMES.PAYMENT_REPORT),
    true,
    dropDownMappingToSelect
  );

  const paymentMethod = useAPIDropdown(
    () => candidateService.resit.opadmin.useGetPaymentModeOptions({}),
    true,
    dropDownMappingToSelect
  );

  const paymentStatus = useAPIDropdown(
    () => candidateService.paymentReport.opadmin.useGetPaymentStatuses(),
    true,
    dropDownMappingToSelect
  );

  return {
    transactionType,
    privateCentre,
    examLevel,
    paymentMethod,
    paymentStatus,
  };
};

const useSearchAndFilter = (
  state: TableState,
  initData: any,
  tableDispatch: React.Dispatch<TableActionTypes>,
  onHandleFetchingTableData: () => void
) => {
  const { mutate, isLoading } = candidateService.paymentReport.opadmin.usePostPaymentReportSearch(
    (res) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: res.data.content,
          count: res.data.totalElements,
        },
      } as TableActionTypes);
      onHandleFetchingTableData();
    },
    () => null
  );

  const triggerSearch = () => {
    mutate({
      transactionType: getIndexValue(state.filters.transactionType, 0),
      organisationUuid: getIndexValue(state.filters.organisationName, 0),
      examLevelCode: state.filters.examLevel || [],
      paymentMethodCode: state.filters.paymentMethod || [],
      paymentStatus: state.filters.paymentStatus || [],
      q: state.search ?? '',
      startDate: initData.startDate,
      endDate: initData.endDate,
      order: state.order,
      sort: state.sortColumn,
      page: state.page,
      pageSize: state.size,
    });
  };

  useEffect(
    () => {
      if (initData.startDate !== '' && initData.endDate !== '') {
        if (state.page === 0) {
          triggerSearch();
        } else {
          tableDispatch({
            type: 'UPDATE_PAGE',
            payload: 0,
          } as TableActionTypes);
        }
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [initData]
  );

  useEffect(
    () => {
      if (initData.startDate !== '' && initData.endDate !== '') {
        triggerSearch();
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [
      state.size,
      state.page,
      state.sortColumn,
      state.order,
      state.search,
      state.filters.transactionType,
      state.filters.organisationName,
      state.filters.examLevel,
      state.filters.paymentMethod,
      state.filters.paymentStatus,
    ]
  );

  const {
    dialogExportState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadCSV,
    onDialogExportClose,
    disableExportButton,
  } = useExportDownloadAction(state.data);

  return {
    dialogExportState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadCSV,
    onDialogExportClose,
    disableExportButton,
    isLoading,
  };
};

const useExportDownloadAction = (data: any[] | undefined) => {
  const [fileDownloadStatus, setFileDownloadStatus] = useState<FileUploadState>(
    FileUploadState.IDLE
  );
  const [dialogExportState, setDialogExportState] = useState<boolean>(false);

  const [fileUUID, setFileUUID] = useState('');

  const {
    mutate: exportMutate,
  } = candidateService.paymentReport.opadmin.usePostPaymentReportExportExcel(
    (response) => {
      setFileUUID(response.data.uuid);
    },
    (_) => null
  );

  /** to define bulk download
   * */
  const onHandleExport = (inputRequest: PaymentReportSearchRequest) => {
    setDialogExportState(true);
    exportMutate(inputRequest);
  };

  /** handle download action
   * */
  const downloadCSV = (downloadResponse: RetrieveS3URLResponse) => {
    setDialogExportState(true);
    if (downloadResponse.fileStatus === FileUploadState.SUCCESS) {
      setFileUUID('');
      setDialogExportState(false);
    }
    setFileDownloadStatus(downloadResponse.fileStatus as FileUploadState);
  };

  /** handle dialog close
   * */
  const onDialogExportClose = () => {
    setDialogExportState(false);
    setFileUUID('');
  };

  return {
    dialogExportState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadCSV,
    onDialogExportClose,
    disableExportButton: dialogExportState || data === undefined || data.length === 0,
  };
};

const PaymentReportSearchAndFilter = ({
  initData,
  isFetchingTableData,
  onHandleFetchingTableData,
}: {
  initData: SearchDate;
  isFetchingTableData: boolean;
  onHandleFetchingTableData: () => void;
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const {
    examLevel,
    paymentMethod,
    transactionType,
    privateCentre,
    paymentStatus,
  } = useRawFilters();

  const { tableColumns, state, tableDispatch, tOptions } = useDataTableColumns({
    data: {
      examLevel,
      paymentMethod,
      transactionType,
      privateCentre,
      paymentStatus,
    },
    classes,
  });

  const {
    dialogExportState,
    fileDownloadStatus,
    fileUUID,
    onHandleExport,
    downloadCSV,
    onDialogExportClose,
    disableExportButton,
    isLoading,
  } = useSearchAndFilter(state, initData, tableDispatch, onHandleFetchingTableData);

  /** append server size pagination to default column options
   * */
  const appendedServerSizePaginationColumnOption = useMemo<MUIDataTableOptions>(() => {
    const customizeToolBar = () => {
      if (state.count === 0) {
        return (
          <Button variant="text" color="primary" disabled>
            {t('admin->>PaymentReport.ExportAll')}
          </Button>
        );
      }
      return (
        <DownloadFromS3Action
          id="button-export-all"
          color="primary"
          actionType="link"
          requestRoute={{ data: fileUUID, url: '/api/s3/files' }}
          label={t('admin->>PaymentReport.ExportAll')}
          onClick={() => {
            onHandleExport({
              transactionType: getIndexValue(state.filters.transactionType, 0),
              organisationUuid: getIndexValue(state.filters.organisationName, 0),
              examLevelCode: state.filters.examLevel || [],
              paymentMethodCode: state.filters.paymentMethod || [],
              paymentStatus: state.filters.paymentStatus || [],
              q: state.search ?? '',
              startDate: initData.startDate,
              endDate: initData.endDate,
            });
          }}
          onDownload={(status) => downloadCSV(status)}
          disable={disableExportButton}
        />
      );
    };

    return {
      filter: true,
      filterType: 'multiselect',
      pagination: true,
      sort: true,
      customToolbar: customizeToolBar,
      selectToolbarPlacement: 'none',
    };
  }, [
    disableExportButton,
    initData.endDate,
    initData.startDate,
    state.count,
    t,
    state.filters.transactionType,
    state.filters.examLevel,
    state.filters.paymentMethod,
    state.filters.paymentStatus,
    state.filters.organisationName,
    state.search,
    downloadCSV,
    fileUUID,
    onHandleExport,
  ]);

  return (
    <>
      <Box className={classes.searchContainer}>
        <SearchBar
          className={classes.searchInput}
          placeholder={t('admin->>PaymentReport.SearchPlaceholder')}
          name="searchInput"
          ariaLabel="searchInput"
          defaultValue={state.search}
          onSearch={(searchStr: string) => {
            tableDispatch({
              type: 'UPDATE_SEARCH',
              payload: searchStr,
            });
          }}
        />
      </Box>
      <Box>
        <MUIDataTable
          // @ts-ignore
          state={state}
          dispatch={tableDispatch}
          columns={tableColumns}
          data={state.data}
          isLoading={isFetchingTableData || isLoading}
          options={{ ...tOptions, ...appendedServerSizePaginationColumnOption }}
        />
      </Box>

      <DownloadAuditProgressModal
        fileDownloadStatus={fileDownloadStatus}
        modalState={dialogExportState}
        onDialogClose={onDialogExportClose}
      />
    </>
  );
};

export default PaymentReportSearchAndFilter;
